// =====================================================================
// src/core/ui/toast.ts
//
// Sistema de notificaciones toast (vanilla TS, sin librerías).
//
// Filosofía:
//   - API minimalista: showToast(message, type, options?).
//   - Container singleton on-demand (no nodos huérfanos en DOM).
//   - Stacking automático (múltiples toasts se apilan).
//   - Auto-dismiss con duración por tipo.
//   - Anti-XSS: escapeHtml en mensajes.
//   - Estilos via tokens CSS (consumir --color-state-*).
//
// Uso:
//   showToast('Plan creado correctamente', 'success');
//   showToast('Error al guardar', 'error');
//   const id = showToast('Procesando...', 'info', { duration: 0 });
//   dismissToast(id);  // dismiss manual
//
// Autor: Simplificalo Consulting LLC
// =====================================================================

// ─── TIPOS ───────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  /** Duración en ms antes de auto-dismiss. 0 = no auto-dismiss (manual). */
  readonly duration?: number;
  /** Acción opcional con label y handler (ej: "Deshacer"). */
  readonly action?: {
    readonly label: string;
    readonly onClick: () => void;
  };
}

interface InternalToast {
  readonly id: string;
  readonly element: HTMLElement;
  readonly timer: number | null;
}

// ─── ESTADO INTERNO ──────────────────────────────────────────────────

const TOAST_CONTAINER_ID = 'rb-toast-container';
const activeToasts = new Map<string, InternalToast>();
let nextId = 0;

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
};

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '!',
  info: 'i',
};

// ─── HELPERS ─────────────────────────────────────────────────────────

/** Genera id único para cada toast. */
const generateId = (): string => {
  nextId += 1;
  return `toast-${nextId}`;
};

/** Escapa HTML para prevenir XSS en mensajes. */
const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/** Crea o trae el container singleton. */
const getOrCreateContainer = (): HTMLElement => {
  const existing = document.getElementById(TOAST_CONTAINER_ID);
  if (existing) return existing;

  const container = document.createElement('div');
  container.id = TOAST_CONTAINER_ID;
  container.setAttribute('role', 'region');
  container.setAttribute('aria-label', 'Notificaciones');
  container.setAttribute('aria-live', 'polite');
  document.body.appendChild(container);
  return container;
};

/** Construye el elemento DOM de un toast. */
const buildToastElement = (
  id: string,
  message: string,
  type: ToastType,
  action: ToastOptions['action'],
): HTMLElement => {
  const toast = document.createElement('div');
  toast.id = id;
  toast.className = `rb-toast rb-toast--${type}`;
  toast.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');

  const icon = document.createElement('span');
  icon.className = 'rb-toast__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = ICONS[type];

  const text = document.createElement('span');
  text.className = 'rb-toast__message';
  text.innerHTML = escapeHtml(message);

  const close = document.createElement('button');
  close.className = 'rb-toast__close';
  close.setAttribute('aria-label', 'Cerrar notificación');
  close.type = 'button';
  close.textContent = '×';
  close.addEventListener('click', () => {
    dismissToast(id);
  });

  toast.appendChild(icon);
  toast.appendChild(text);

  if (action) {
    const actionBtn = document.createElement('button');
    actionBtn.className = 'rb-toast__action';
    actionBtn.type = 'button';
    actionBtn.textContent = action.label;
    actionBtn.addEventListener('click', () => {
      action.onClick();
      dismissToast(id);
    });
    toast.appendChild(actionBtn);
  }

  toast.appendChild(close);
  return toast;
};

// ─── API PÚBLICA ─────────────────────────────────────────────────────

/**
 * Muestra un toast. Retorna el id del toast (útil para dismiss manual).
 *
 * @param message Mensaje a mostrar (texto plano, se escapa HTML).
 * @param type Tipo: success | error | warning | info.
 * @param options Configuración opcional (duración, acción).
 *
 * @example
 *   showToast('Plan creado', 'success');
 *   showToast('Error al guardar', 'error');
 *   showToast('Procesando...', 'info', { duration: 0 });
 *   showToast('Plan eliminado', 'success', {
 *     action: { label: 'Deshacer', onClick: () => restorePlan() },
 *   });
 */
export const showToast = (message: string, type: ToastType, options?: ToastOptions): string => {
  const id = generateId();
  const duration = options?.duration ?? DEFAULT_DURATIONS[type];
  const element = buildToastElement(id, message, type, options?.action);

  const container = getOrCreateContainer();
  container.appendChild(element);

  // Trigger animación de entrada (next frame).
  requestAnimationFrame(() => {
    element.classList.add('rb-toast--visible');
  });

  let timer: number | null = null;
  if (duration > 0) {
    timer = window.setTimeout(() => {
      dismissToast(id);
    }, duration);
  }

  activeToasts.set(id, { id, element, timer });
  return id;
};

/**
 * Cierra un toast manualmente por id.
 * Si el id no existe (ya fue dismissed), no hace nada.
 */
export const dismissToast = (id: string): void => {
  const toast = activeToasts.get(id);
  if (toast === undefined) return;

  if (toast.timer !== null) {
    window.clearTimeout(toast.timer);
  }

  // Animación de salida.
  toast.element.classList.remove('rb-toast--visible');
  toast.element.classList.add('rb-toast--leaving');

  // Remover del DOM después de la animación (300ms es suficiente).
  window.setTimeout(() => {
    toast.element.remove();
    activeToasts.delete(id);

    // Si era el último, remover el container.
    if (activeToasts.size === 0) {
      const container = document.getElementById(TOAST_CONTAINER_ID);
      container?.remove();
    }
  }, 300);
};

/**
 * Cierra TODOS los toasts visibles. Útil al navegar entre páginas
 * para limpiar mensajes obsoletos.
 *
 * @example
 *   onRouteChange(() => dismissAllToasts());
 */
export const dismissAllToasts = (): void => {
  const ids = Array.from(activeToasts.keys());
  for (const id of ids) {
    dismissToast(id);
  }
};

// ─── HELPERS DE CONVENIENCIA ─────────────────────────────────────────

/** Atajo: showToast(msg, 'success'). */
export const toastSuccess = (message: string, options?: ToastOptions): string => {
  return showToast(message, 'success', options);
};

/** Atajo: showToast(msg, 'error'). */
export const toastError = (message: string, options?: ToastOptions): string => {
  return showToast(message, 'error', options);
};

/** Atajo: showToast(msg, 'warning'). */
export const toastWarning = (message: string, options?: ToastOptions): string => {
  return showToast(message, 'warning', options);
};

/** Atajo: showToast(msg, 'info'). */
export const toastInfo = (message: string, options?: ToastOptions): string => {
  return showToast(message, 'info', options);
};
